import { inject, Injectable, signal } from '@angular/core';
import { PageEvent } from '@angular/material/paginator';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SortDirection } from '@angular/material/sort';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import axios, { AxiosError } from 'axios';
import { format, isValid, parseISO } from 'date-fns';
import {
  BehaviorSubject,
  debounceTime,
  distinctUntilChanged,
  map,
  merge,
  skipUntil,
  startWith,
  Subject,
  takeUntil,
} from 'rxjs';
import { PayeeCasesService } from 'src/app/shared/payee-cases/payee-cases.service';
import { UNKNOWN_CLIENT_NAME } from 'src/app/shared/payee-cases/types';
import { environment } from 'src/environments/environment';
import { z } from 'zod';
import { GetEventsResponse } from './types';

@Injectable({
  providedIn: 'root',
})
export class EventsService {
  private route = inject(ActivatedRoute);
  private snackbar = inject(MatSnackBar);
  private translate = inject(TranslateService);
  private payeeCasesService = inject(PayeeCasesService);

  private readonly destroy = new Subject<void>();
  readonly setEventsSubject = new Subject<void>();

  readonly payee_case_reference_id = new BehaviorSubject<string | null>(null);
  readonly debtor_name = new BehaviorSubject<string | null>(null);
  readonly label = new BehaviorSubject<string | null>(null);
  readonly happened_at_min = new BehaviorSubject<string | Date | null>(null);
  readonly happened_at_max = new BehaviorSubject<string | Date | null>(null);

  readonly sort_by = new BehaviorSubject<string | null>('happened_at');
  readonly sort_direction = new BehaviorSubject<SortDirection>('desc');

  readonly perPage = new BehaviorSubject<number>(10);
  readonly page = new BehaviorSubject<number>(1);

  readonly pagination = merge(
    this.page,
    this.perPage,
    this.sort_by,
    this.sort_direction,
  );

  readonly filters = merge(
    this.payee_case_reference_id,
    this.debtor_name,
    this.label,
    this.happened_at_min,
    this.happened_at_max,
    this.payeeCasesService.client,
  );

  readonly filtersWithPagination = merge(this.pagination, this.filters);

  readonly events = new Subject<GetEventsResponse>();
  readonly loading = signal(false);

  private eventsFetchGeneration = 0;

  onInit(): void {
    this.filtersWithPagination
      .pipe(takeUntil(this.destroy))
      .subscribe(() => this.setEventsSubject.next());

    this.loadFiltersFromQueryParams();

    this.filters
      .pipe(skipUntil(this.events), takeUntil(this.destroy))
      .subscribe(() => {
        if (this.page.value !== 1) {
          this.page.next(1);
        }
      });

    this.loading.set(true);

    this.setEventsSubject
      .pipe(
        takeUntil(this.destroy),
        startWith(this.getFilters()),
        debounceTime(200),
        map(() => JSON.stringify(this.getFilters())),
        distinctUntilChanged(),
      )
      .subscribe(async () => {
        await this.setEvents();
      });
  }

  onDestroy(): void {
    this.destroy.next();
  }

  setPagination(event: PageEvent): EventsService {
    if (this.perPage.value === event.pageSize) {
      this.page.next(event.pageIndex + 1);
    } else {
      this.page.next(1);
    }
    this.perPage.next(event.pageSize);

    return this;
  }

  private loadFiltersFromQueryParams(): void {
    const params = this.route.snapshot.queryParams;

    this.payee_case_reference_id.next(params.payee_case_reference_id || null);
    this.debtor_name.next(params.debtor_name || null);
    this.label.next(params.label || null);

    const clientId = params.client_id;
    if (z.string().uuid().safeParse(clientId).success) {
      const current = this.payeeCasesService.client.value;
      if (current?.uuid !== clientId) {
        this.payeeCasesService.client.next({
          uuid: clientId,
          name: UNKNOWN_CLIENT_NAME,
        });
      }
    }

    const happenedAtMin = params.happenedAtMin;
    if (happenedAtMin && isValid(parseISO(happenedAtMin))) {
      this.happened_at_min.next(happenedAtMin);
    }

    const happenedAtMax = params.happenedAtMax;
    if (happenedAtMax && isValid(parseISO(happenedAtMax))) {
      this.happened_at_max.next(happenedAtMax);
    }

    const sort_by = params.sort_by;
    if (sort_by === 'happened_at') {
      this.sort_by.next(sort_by);

      let sort_direction = params.sort_direction;
      if (!['asc', 'desc'].includes(sort_direction)) {
        sort_direction = 'desc';
      }
      this.sort_direction.next(sort_direction);
    }

    const page = parseInt(params.page, 10);
    if (!Number.isNaN(page)) {
      this.page.next(Math.max(page, 1));
    }

    const perPage = parseInt(params.perPage, 10);
    if (!Number.isNaN(perPage)) {
      this.perPage.next(Math.max(perPage, 1));
    }
  }

  resetFilters(): EventsService {
    this.payee_case_reference_id.next(null);
    this.debtor_name.next(null);
    this.label.next(null);
    this.happened_at_min.next(null);
    this.happened_at_max.next(null);
    this.perPage.next(10);
    this.page.next(1);

    return this;
  }

  getFilters(): Record<string, string | number | null> {
    let happenedAtMin = this.happened_at_min.value;
    if (happenedAtMin instanceof Date) {
      happenedAtMin = format(happenedAtMin, 'yyyy-MM-dd');
    }

    let happenedAtMax = this.happened_at_max.value;
    if (happenedAtMax instanceof Date) {
      happenedAtMax = format(happenedAtMax, 'yyyy-MM-dd');
    }

    return {
      payee_case_reference_id: this.payee_case_reference_id.value,
      debtor_name: this.debtor_name.value,
      label: this.label.value,
      happenedAtMin,
      happenedAtMax,
      page: this.page.value,
      sort_by: this.sort_direction.value ? this.sort_by.value : null,
      sort_direction: this.sort_direction.value || null,
      perPage: this.perPage.value,
      client_id: this.payeeCasesService.client.value?.uuid ?? null,
    };
  }

  async setEvents(): Promise<void> {
    const generation = ++this.eventsFetchGeneration;

    try {
      this.loading.set(true);

      const params = Object.fromEntries(
        Object.entries(this.getFilters()).filter(
          ([, value]) => value !== null && value !== '',
        ),
      );

      const result = await axios.get<GetEventsResponse>(
        `${environment.baseUrl}/api/case-events`,
        {
          params,
        },
      );

      if (generation !== this.eventsFetchGeneration) {
        return;
      }

      this.events.next(result.data);
    } catch (error: AxiosError | unknown) {
      if (generation !== this.eventsFetchGeneration) {
        return;
      }

      if (error instanceof AxiosError) {
        console.error(
          'Network error while getting events table',
          error.response?.data,
        );
      } else {
        console.error('Unknown error while getting events table', error);
      }

      this.snackbar.open(
        this.translate.instant('EVENTS.ERROR_WHILE_LOADING'),
        this.translate.instant('OK'),
        {
          duration: 5000,
        },
      );
    } finally {
      if (generation === this.eventsFetchGeneration) {
        this.loading.set(false);
      }
    }
  }
}
